import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useAuth, useCanManageAnyTicket } from "../app/auth/hooks";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TabsTriggerCount,
} from "../app/components/primitives/tabs";
import {
  ArrowLeft,
  CheckCircle,
  Calendar,
  Phone,
  Mail,
  Users,
  FileText,
  Pencil,
  UserCog,
  Briefcase,
  FileSpreadsheet,
  AlertTriangle,
  Building2,
  UserCheck,
  Megaphone,
} from "lucide-react";
import { campaignTicketLabel } from "@shared-core/domain/campaign-ticket-label";
import type * as API from "@shared-core/data/api-types";
import { postTicketComment } from "@shared-core/domain/post-ticket-comment";
import { ticketStatusAvailability } from "@shared-core/domain/ticket-status-availability";
import type { BuddyListEntry } from "@shared-core/data/adapter";
import type { CampaignEngagementContact } from "@shared-core/data/marketing-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
  formatEscalatesIn,
  formatWallClockOrDash,
  toDatetimeInputValue,
} from "@shared-core/domain/formatters";
import {
  STATUS_CONFIG,
  TicketMetadataStrip,
  type AssignableUser,
  type TicketStatusValue,
} from "../app/components/domain/tickets/TicketMetadataStrip";
import { CreditHoldDecisionPanel } from "../app/components/domain/credit-control/CreditHoldDecisionPanel";
import { Badge } from "../app/components/primitives/badge";
import {
  EntityCombobox,
  type EntityOption,
} from "../app/components/patterns/EntityCombobox";
import { TicketActivityTimeline } from "../app/components/domain/tickets/TicketActivityTimeline";
import { TicketComments } from "../app/components/domain/tickets/TicketComments";
import { TicketCalendarButton } from "../app/components/domain/tickets/TicketCalendarAction";
import { TicketAlertMuteControl } from "../app/components/domain/tickets/TicketAlertMuteControl";
import {
  CampaignTicketSection,
  EarlierCampaignTicketBanner,
} from "../app/components/domain/tickets/CampaignTicketSection";

const ACTION_TYPE_CONFIG = {
  general: { label: "General", icon: FileText },
  call: { label: "Call", icon: Phone },
  email: { label: "Email", icon: Mail },
  meeting: { label: "Meeting", icon: Users },
};

// subject_types marketing_campaigns_module raises tickets under. campaign_contact
// (one ticket per contact per campaign) replaces campaign_engaged and the
// step-keyed campaign tickets; the older ones stay until they are closed.
const CAMPAIGN_TICKET_SUBJECT_TYPES = new Set([
  "campaign_contact",
  "campaign",
  "campaign_engaged",
  "campaign_bounce",
]);

// subject_types an automated flow raises from an alert — mirrors
// workflow_module.MUTABLE_ALERT_TICKET_SUBJECT_TYPES (customer-alert-
// suppression feature). Only these tickets can be muted.
const MUTABLE_ALERT_TICKET_SUBJECT_TYPES = new Set([
  "account_health",
  "critical_alert",
  "alert",
]);

// A purchase invoice's query (Query with colleague…), and an authorisation request
// open when PIM-6 turned them into queries: the colleague answers with Answer query…,
// a command on the ticket's One record, so Classic links there.
const QUERY_SUBJECT_TYPES = new Set([
  "purchase_invoice",
  "variance_authorisation",
]);
const ANSWER_QUERY_IN_ONE = "Answer this query in One";

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const { user } = useAuth();
  const canManageAnyTicket = useCanManageAnyTicket();

  // department-ticket-routing: this user's own department membership —
  // the Claim button must never render for a non-member (it would 403), so
  // it's gated on this rather than showing unconditionally whenever a
  // ticket has an owning department.
  const [myDepartments, setMyDepartments] = useState<API.Department[]>([]);
  const [isClaiming, setIsClaiming] = useState(false);
  // Two people clicking Claim at once is the expected race, not an edge
  // case — 409 gets this distinct message ("Already claimed by X") instead
  // of a generic failure, and the optimistic assignment below rolls back
  // visibly via the loadTicket() refresh that follows it.
  const [claimConflictMessage, setClaimConflictMessage] = useState<
    string | null
  >(null);

  const [ticket, setTicket] = useState<API.TicketRecordView | null>(null);
  const [loadStatus, setLoadStatus] = useState<
    "loading" | "ready" | "not_found"
  >("loading");
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignCustomerError, setAssignCustomerError] = useState<string | null>(
    null,
  );
  const [actionType, setActionType] = useState<
    "call" | "email" | "meeting" | "general"
  >("general");

  const [activeTab, setActiveTab] = useState<"details" | "activity">("details");
  const [categories, setCategories] = useState<API.TicketCategory[]>([]);
  const [tags, setTags] = useState<API.TicketTag[]>([]);
  const [watchers, setWatchers] = useState<API.TicketWatcher[]>([]);
  const [activity, setActivity] = useState<API.TicketActivity[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  // People who have left: never offered as an assignee, but a ticket's history still names them.
  const [departedUsers, setDepartedUsers] = useState<AssignableUser[]>([]);

  const [linkedJob, setLinkedJob] = useState<API.JobViewItem | null>(null);
  const [linkedEstimate, setLinkedEstimate] = useState<API.Estimate | null>(
    null,
  );

  // Campaign engagement (opens/clicks) for this ticket's contact — only
  // meaningful for campaign-raised tickets, whose subject_id encodes
  // "{campaign_id}:{person_id}".
  const [campaignEngagementContact, setCampaignEngagementContact] =
    useState<CampaignEngagementContact | null>(null);

  // The campaign side of a campaign contact ticket (its sends and outcome).
  // Null until loaded, and for any other ticket.
  const [campaignTicket, setCampaignTicket] =
    useState<API.CampaignTicketContext | null>(null);

  // Every other ticket about this ticket's contact — sales reps get
  // hounded-contact visibility on the ticket itself, not just on the
  // contact's own page. Keyed on contact_id (the broader Tharstern-contact
  // link) when set, else person_id (campaign-flow-only) — either way, not
  // subject_id, so it also catches non-campaign tickets about the same
  // contact.
  const [relatedContactTickets, setRelatedContactTickets] = useState<
    API.Ticket[]
  >([]);

  // Outcome banner data — resolved server-side (GET /tickets/{id}/outcome-context,
  // see docs/features/ticket-contact-related-tickets/). `null` covers both
  // "not loaded yet" and "no related resolved ticket exists" — the banner
  // simply doesn't render for either, same as campaignEngagementContact below.
  const [ticketOutcomeContext, setTicketOutcomeContext] =
    useState<API.TicketOutcomeContext | null>(null);

  // Ticket covering (csr-ticket-routing V2: manual covering) — the CSR's
  // own buddy list, scoped from the full roster since only they may cover
  // this ticket (server-side enforced too, see BuddyNotOnListError).
  const [csrBuddyList, setCsrBuddyList] = useState<BuddyListEntry | null>(null);
  // Distinguishes "still loading the CSR's buddy list" from "loaded, and the
  // CSR has no buddies" so the manager's proactive empty-state guidance
  // ("add buddies in Settings") only shows once the fetch has resolved.
  const [csrBuddyListLoaded, setCsrBuddyListLoaded] = useState(false);
  const [selectedCoveringUserId, setSelectedCoveringUserId] = useState("");
  const [isAssigningCovering, setIsAssigningCovering] = useState(false);
  const [isChangingCovering, setIsChangingCovering] = useState(false);

  const loadTicket = useCallback(async () => {
    if (!id) return;
    setLoadStatus("loading");
    const found = await dataAdapter.getTicketView(id);
    if (!found) {
      setLoadStatus("not_found");
      return;
    }
    setTicket(found);
    setTitle(found.title);
    setDescription(found.description ?? "");
    setDueDate(toDatetimeInputValue(found.due_date));
    setActionType(
      (found.action_type as "call" | "email" | "meeting" | "general" | null) ??
        "general",
    );
    setLoadStatus("ready");
    // Not ownership-gated — stamp on every open regardless of who's
    // viewing; the action itself is a no-op once already set.
    if (!found.first_viewed_at) {
      dataAdapter.markTicketViewed(found.id).catch((error) => {
        console.error("tickets.mark_viewed.failed", {
          ticket_id: found.id,
          exc_type: error instanceof Error ? error.name : typeof error,
        });
      });
    }
  }, [dataAdapter, id]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  // department-ticket-routing: see myDepartments above for why this gates
  // the Claim button.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    dataAdapter
      .getDepartmentsForUser(user.id)
      .then((depts) => {
        if (!cancelled) setMyDepartments(depts);
      })
      .catch((error) => {
        console.error("tickets.my_departments.fetch_failed", {
          exc_type: error instanceof Error ? error.name : typeof error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, user?.id]);

  useEffect(() => {
    let cancelled = false;
    async function loadGlobalMetadata() {
      const [categoryList, userPage, departedPage] = await Promise.all([
        dataAdapter.listTicketCategories(),
        dataAdapter.listUsers(),
        dataAdapter.listUsers({ state: "left" }),
      ]);
      if (cancelled) return;
      setCategories(categoryList);
      setAssignableUsers(
        userPage.items.map((u) => ({ id: u.id, name: u.name })),
      );
      setDepartedUsers(
        departedPage.items.map((u) => ({ id: u.id, name: u.name })),
      );
    }
    loadGlobalMetadata();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  useEffect(() => {
    if (!ticket) return;
    let cancelled = false;
    async function loadTicketMetadata() {
      const [tagList, watcherList, activityResult] = await Promise.all([
        dataAdapter.getTicketTags(ticket!.id),
        dataAdapter.getTicketWatchers(ticket!.id),
        dataAdapter.getTicketActivity(ticket!.id, { pageSize: 100 }),
      ]);
      if (cancelled) return;
      setTags(tagList);
      setWatchers(watcherList);
      setActivity(activityResult.items);
    }
    loadTicketMetadata();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on ticket id; re-running on every ticket field change would refetch tags/watchers/activity needlessly
  }, [dataAdapter, ticket?.id]);

  useEffect(() => {
    if (!ticket?.id || ticket.subject_type !== "campaign_contact") {
      setCampaignTicket(null);
      return;
    }
    let cancelled = false;
    dataAdapter
      .getCampaignTicketContext(ticket.id)
      .then((context) => {
        if (!cancelled) setCampaignTicket(context);
      })
      .catch((error) => {
        console.error("tickets.campaign_ticket_context.fetch_failed", {
          ticket_id: ticket.id,
          exc_type: error instanceof Error ? error.name : typeof error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, ticket?.id, ticket?.subject_type]);

  async function handleRecordCampaignOutcome(
    outcome: API.CallOutcome,
    note?: string,
  ) {
    if (!ticket) return;
    try {
      setCampaignTicket(
        await dataAdapter.recordCampaignTicketOutcome(ticket.id, outcome, note),
      );
    } catch (error) {
      console.error("tickets.campaign_ticket_outcome.record_failed", {
        ticket_id: ticket.id,
        outcome,
        exc_type: error instanceof Error ? error.name : typeof error,
      });
      throw error;
    }
  }

  async function handleClearCampaignOutcome() {
    if (!ticket) return;
    try {
      setCampaignTicket(
        await dataAdapter.clearCampaignTicketOutcome(ticket.id),
      );
    } catch (error) {
      console.error("tickets.campaign_ticket_outcome.clear_failed", {
        ticket_id: ticket.id,
        exc_type: error instanceof Error ? error.name : typeof error,
      });
      throw error;
    }
  }

  // Campaign engagement (opens/clicks) for campaign-raised tickets. subject_id
  // encodes "{campaign_id}:{person_id}" — see marketing_campaigns_module's
  // raise_engagement_tickets/raise_bounce_tickets.
  useEffect(() => {
    if (
      !ticket ||
      !ticket.subject_type ||
      !CAMPAIGN_TICKET_SUBJECT_TYPES.has(ticket.subject_type) ||
      !ticket.subject_id
    ) {
      setCampaignEngagementContact(null);
      return;
    }
    const [campaignId, personId] = ticket.subject_id.split(":");
    if (!campaignId || !personId) return;
    let cancelled = false;
    dataAdapter
      .getCampaignEngagement(campaignId)
      .then((engagement) => {
        if (cancelled) return;
        const contact =
          engagement?.contacts.find((c) => c.person_id === personId) ?? null;
        setCampaignEngagementContact(contact);
      })
      .catch((error) => {
        console.error("tickets.campaign_engagement.fetch_failed", {
          ticket_id: ticket.id,
          exc_type: error instanceof Error ? error.name : typeof error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, ticket]);

  useEffect(() => {
    // customer_person_id (the broader, rep-settable CIS person link) takes
    // precedence when present; person_id (campaign-flow-only) is the
    // fallback for tickets with no contact link yet.
    if (ticket?.customer_person_id == null && !ticket?.person_id) {
      setRelatedContactTickets([]);
      return;
    }
    let cancelled = false;
    dataAdapter
      .getTickets(
        ticket?.customer_person_id != null
          ? { customerPersonId: ticket.customer_person_id, pageSize: 10 }
          : { personId: ticket!.person_id!, pageSize: 10 },
      )
      .then((result) => {
        if (cancelled) return;
        setRelatedContactTickets(
          result.items.filter((t) => t.id !== ticket!.id),
        );
      })
      .catch((error) => {
        console.error("tickets.related_contact_tickets.fetch_failed", {
          ticket_id: ticket!.id,
          exc_type: error instanceof Error ? error.name : typeof error,
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on the three narrow fields this effect reads; other ticket field changes shouldn't refetch related tickets
  }, [dataAdapter, ticket?.id, ticket?.person_id, ticket?.customer_person_id]);

  // The outcome-context banner's data — one composed response, resolved
  // server-side. See the state declaration above for why this replaced a
  // client-side join of getTickets + getTicketActivity.
  useEffect(() => {
    // No contact link means the backend can never find a related ticket —
    // same guard the sibling relatedContactTickets effect above uses.
    if (!ticket || (ticket.customer_person_id == null && !ticket.person_id)) {
      setTicketOutcomeContext(null);
      return;
    }
    const ticketId = ticket.id;
    // Drop the previous ticket's context before fetching this one's.
    // loadTicket deliberately keeps the old ticket rendered across a refetch
    // (so an in-flight comment draft survives), so without this the banner
    // shows the *previous* ticket's related-resolved ticket for one round
    // trip — and since the banner's own link is how a rep moves between two
    // tickets for the same contact, that means arriving on a page whose
    // banner tells you to go check the page you are already on. Clearing on
    // the error path too: a failed read must not leave a stale banner up
    // indefinitely.
    setTicketOutcomeContext(null);
    let cancelled = false;
    dataAdapter
      .getTicketOutcomeContext(ticketId)
      .then((result) => {
        if (cancelled) return;
        setTicketOutcomeContext(result);
      })
      .catch((error) => {
        console.error("tickets.outcome_context.fetch_failed", {
          ticket_id: ticketId,
          exc_type: error instanceof Error ? error.name : typeof error,
        });
        if (cancelled) return;
        setTicketOutcomeContext(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on the three narrow fields this effect reads; other ticket field changes shouldn't refetch the outcome context
  }, [dataAdapter, ticket?.id, ticket?.person_id, ticket?.customer_person_id]);

  // Linked job/estimate aren't part of TicketViewItem (only linked_alert/
  // linked_opportunity are) — fetched on demand the same way TicketCard
  // used to before this page existed.
  useEffect(() => {
    if (!ticket) return;
    let cancelled = false;
    async function loadLinkedEntity() {
      if (ticket!.subject_type === "job" && ticket!.subject_id) {
        const job = await dataAdapter.getChargeableJobById(ticket!.subject_id!);
        if (!cancelled) setLinkedJob(job);
      } else if (ticket!.subject_type === "estimate" && ticket!.subject_id) {
        const estimate = await dataAdapter.getCurrentEstimateByHeaderRef(
          ticket!.subject_id!,
        );
        if (!cancelled) setLinkedEstimate(estimate);
      }
    }
    loadLinkedEntity();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, ticket]);

  const resolveUserName = (userId: string) =>
    (
      assignableUsers.find((u) => u.id === userId) ??
      departedUsers.find((u) => u.id === userId)
    )?.name ?? userId;

  // csr-ticket-routing V2: only fetched for managers/admins, since only
  // they can act on it — the CSR's own buddy list is the candidate roster
  // for who may cover this ticket while they're away.
  useEffect(() => {
    if (!ticket?.assigned_to_user_id || !canManageAnyTicket) {
      setCsrBuddyList(null);
      setCsrBuddyListLoaded(false);
      return;
    }
    let cancelled = false;
    setCsrBuddyListLoaded(false);
    async function loadCsrBuddyList() {
      const lists = await dataAdapter.listBuddyLists();
      if (cancelled) return;
      setCsrBuddyList(
        lists.find((b) => b.userId === ticket!.assigned_to_user_id) ?? null,
      );
      setCsrBuddyListLoaded(true);
    }
    loadCsrBuddyList();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on the CSR's id, not the whole ticket; other ticket field changes shouldn't re-fetch the buddy list
  }, [dataAdapter, ticket?.assigned_to_user_id, canManageAnyTicket]);

  const handleUpdate = async (updates: API.TicketUpdateRequest) => {
    if (!ticket) return;
    await dataAdapter.updateTicket(ticket.id, updates);
    await loadTicket();
  };

  const handleSaveEdit = async () => {
    if (!title.trim()) return;
    await handleUpdate({
      title: title.trim(),
      description: description.trim() || null,
      due_date: dueDate || null,
      action_type: actionType,
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    if (!ticket) return;
    setTitle(ticket.title);
    setDescription(ticket.description ?? "");
    setDueDate(toDatetimeInputValue(ticket.due_date));
    setActionType(
      (ticket.action_type as "call" | "email" | "meeting" | "general" | null) ??
        "general",
    );
    setIsEditing(false);
  };

  // A ticket created without a customer (the create form leaves it optional)
  // can be given one afterwards. Only offered while none is set: a customer
  // already on the ticket may carry a contact or linked alert/job of that
  // customer, which a swap would leave pointing at the wrong company.
  const fetchCustomers = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const resp = await dataAdapter.getCustomersForIssuePicker({
        q: q || undefined,
        page: 1,
        pageSize: 25,
      });
      return resp.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [dataAdapter],
  );

  const handleAssignCustomer = async (option: EntityOption | null) => {
    if (!option) return;
    setAssignCustomerError(null);
    try {
      await handleUpdate({ customer_id: option.id });
    } catch {
      setAssignCustomerError("Couldn't add the customer. Please try again.");
    }
  };

  const handleComplete = () => handleUpdate({ status: "resolved" });
  const handleReopen = () => handleUpdate({ status: "open" });

  // department-ticket-routing: claim an unclaimed department ticket.
  // Optimistic-but-verified (frontend update spec §6) — reflect the claim
  // immediately, reconcile on the response, and roll back visibly on 409
  // (two people clicking Claim at once is the expected race, not an edge
  // case — see workflow_module's TicketAlreadyAssignedError).
  const handleClaim = async () => {
    if (!ticket || !user) return;
    setIsClaiming(true);
    setClaimConflictMessage(null);
    const previous = ticket;
    setTicket({ ...ticket, assigned_to_user_id: user.id });
    try {
      await dataAdapter.claimDepartmentTicket(ticket.id);
      await loadTicket();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const refreshed = await dataAdapter.getTicketView(ticket.id);
        setTicket(refreshed ?? previous);
        setClaimConflictMessage(
          `Already claimed by ${refreshed?.assignee_user_name ?? "someone else"}.`,
        );
      } else {
        console.error("tickets.claim.failed", {
          ticket_id: ticket.id,
          exc_type: error instanceof Error ? error.name : typeof error,
        });
        setTicket(previous);
      }
    } finally {
      setIsClaiming(false);
    }
  };

  const handleAddComment = async (body: string, files: File[]) => {
    if (!ticket) throw new Error("Ticket not loaded");
    const result = await postTicketComment(dataAdapter, {
      ticketId: ticket.id,
      customerId: ticket.customer_id ?? undefined,
      body,
      files,
    });
    setActivity((prev) => [
      ...prev,
      { ...result.comment, attachments: result.attachments },
    ]);
    return result;
  };

  const handleDownloadAttachment = async (
    attachment: API.WorkflowAttachment,
  ) => {
    const { blob, filename } = await dataAdapter.downloadWorkflowAttachment(
      attachment.id,
    );
    capabilities.downloadBlob(filename || attachment.filename, blob);
  };

  const handleAddTag = async (tagValue: string) => {
    if (!ticket) return;
    const tag = await dataAdapter.addTicketTag(ticket.id, tagValue);
    setTags((prev) => [...prev, tag]);
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!ticket) return;
    await dataAdapter.removeTicketTag(tagId);
    setTags((prev) => prev.filter((t) => t.id !== tagId));
  };

  const handleReassign = async (toUserId: string) => {
    if (!ticket) return;
    await dataAdapter.delegateTicket(ticket.id, { to_user_id: toUserId });
    const activityResult = await dataAdapter.getTicketActivity(ticket.id, {
      pageSize: 100,
    });
    setActivity(activityResult.items);
  };

  // A credit decision changes the ticket's status and adds comments: refresh
  // both without the full reload, which would unmount the decision panel.
  const handleCreditDecided = async () => {
    if (!ticket) return;
    const [found, activityResult] = await Promise.all([
      dataAdapter.getTicketView(ticket.id),
      dataAdapter.getTicketActivity(ticket.id, { pageSize: 100 }),
    ]);
    if (found) setTicket(found);
    setActivity(activityResult.items);
  };

  const handleAssignCovering = async () => {
    if (!ticket?.assigned_to_user_id || !selectedCoveringUserId) return;
    setIsAssigningCovering(true);
    try {
      await dataAdapter.assignTicketCovering(ticket.id, {
        csrUserId: ticket.assigned_to_user_id,
        coveringUserId: selectedCoveringUserId,
      });
      setSelectedCoveringUserId("");
      setIsChangingCovering(false);
      await loadTicket();
      const activityResult = await dataAdapter.getTicketActivity(ticket.id, {
        pageSize: 100,
      });
      setActivity(activityResult.items);
    } finally {
      setIsAssigningCovering(false);
    }
  };

  const isRequester = ticket?.created_by_user_id === user?.id;
  const isExplicitWatcher = watchers.some((w) => w.user_id === user?.id);
  const isWatching = isRequester || isExplicitWatcher;
  const otherWatcherNames = watchers
    .filter((w) => w.user_id !== user?.id)
    .map((w) => resolveUserName(w.user_id));

  const handleToggleWatch = async () => {
    if (!ticket || !user) return;
    if (isExplicitWatcher) {
      await dataAdapter.removeTicketWatcher(ticket.id, user.id);
      setWatchers((prev) => prev.filter((w) => w.user_id !== user.id));
    } else {
      const watcher = await dataAdapter.addTicketWatcher(ticket.id, user.id);
      setWatchers((prev) => [...prev, watcher]);
    }
  };

  // "No need to watch" — customer-alert-suppression feature. Rethrows so
  // TicketAlertMuteControl's dialog can show the error inline and stay open.
  const handleMuteTicketAlerts = async (reason: string, expiresAt: string) => {
    if (!ticket) return;
    await dataAdapter.muteTicketAlerts(ticket.id, reason, expiresAt);
    await loadTicket();
  };

  const handleClearTicketAlertSuppression = async () => {
    if (!ticket) return;
    await dataAdapter.clearTicketAlertSuppression(ticket.id);
    await loadTicket();
  };

  // Only block on the full-page spinner for the very first load. loadTicket()
  // re-runs after every update (status/priority/category change, delegation,
  // etc.) to refresh derived fields, and it flips loadStatus to "loading" each
  // time — if that unmounted this whole tree on every edit, any in-progress,
  // unposted state below (e.g. a comment draft the user hasn't clicked "Post"
  // on yet) would be wiped by the remount. Once a ticket has loaded once, a
  // background refetch re-renders in place instead.
  if (loadStatus === "loading" && !ticket) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading ticket…</div>
    );
  }

  if (loadStatus === "not_found" || !ticket) {
    return (
      <div className="p-6 space-y-4">
        <Link
          to={paths.tickets()}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to Tickets
        </Link>
        <p className="text-sm text-muted-foreground">
          This ticket doesn't exist, or you don't have access to it.
        </p>
      </div>
    );
  }

  const isCompleted =
    ticket.status === "resolved" || ticket.status === "closed";
  const completedAt = ticket.resolved_at ?? ticket.closed_at ?? null;
  const ActionIcon = ACTION_TYPE_CONFIG[actionType].icon;
  const hasSidebar = !!linkedJob || !!linkedEstimate;

  // department-ticket-routing: Claim only ever shows for an unclaimed
  // department ticket whose viewer is actually a member — never a button
  // that will 403 (frontend update spec §4).
  const isDepartmentMember = myDepartments.some(
    (d) => d.id === ticket.owning_department_id,
  );
  const canClaim =
    !ticket.assigned_to_user_id &&
    !!ticket.owning_department_id &&
    isDepartmentMember;
  const escalationHint =
    !ticket.assigned_to_user_id &&
    ticket.owning_department_id &&
    ticket.escalates_at
      ? formatEscalatesIn(ticket.escalates_at)
      : null;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Tickets
      </button>

      {campaignTicket && (
        <EarlierCampaignTicketBanner context={campaignTicket} />
      )}

      <div className="border border-border rounded-lg bg-background">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full text-lg font-[inherit] bg-transparent border-none outline-none focus:ring-0 p-0 m-0"
                  autoFocus
                />
              ) : (
                <div>
                  <h1
                    className={`text-lg ${isCompleted ? "line-through text-muted-foreground" : ""}`}
                  >
                    {title || "Untitled Ticket"}
                  </h1>
                  {isCompleted && completedAt && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Completed {formatDateOrDash(completedAt)}
                    </p>
                  )}
                </div>
              )}
            </div>
            {!isEditing && !isCompleted && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditing(true)}
                title="Edit ticket"
              >
                <Pencil className="size-4" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="size-3.5" />
              {isEditing ? (
                <Input
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="text-sm h-7 px-2 py-0"
                />
              ) : dueDate ? (
                // dueDate is a datetime-local wall-clock string (see
                // toDatetimeInputValue above) — format it directly rather
                // than through the Europe/London-pinned formatter, or view
                // mode and the edit input would disagree for any viewer
                // whose device isn't set to the UK.
                formatWallClockOrDash(dueDate)
              ) : (
                "No due date"
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <ActionIcon className="size-3.5" />
              {ACTION_TYPE_CONFIG[actionType].label}
            </div>
          </div>

          {ticket.subject_type != null &&
            MUTABLE_ALERT_TICKET_SUBJECT_TYPES.has(ticket.subject_type) && (
              <TicketAlertMuteControl
                alertSuppression={ticket.alert_suppression ?? null}
                onMute={handleMuteTicketAlerts}
                onClear={handleClearTicketAlertSuppression}
              />
            )}

          <TicketMetadataStrip
            status={ticket.status as TicketStatusValue}
            priority={ticket.priority as "low" | "normal" | "high" | "urgent"}
            categoryId={ticket.category_id ?? null}
            categories={categories}
            tags={tags}
            isWatching={isWatching}
            isImplicitWatcher={isRequester}
            otherWatcherNames={otherWatcherNames}
            assignedToUserId={ticket.assigned_to_user_id ?? null}
            assignableUsers={assignableUsers}
            resolveUserName={resolveUserName}
            unavailableStatuses={ticketStatusAvailability(ticket).disabled}
            hiddenStatuses={ticketStatusAvailability(ticket).hidden}
            onChangeStatus={(status) => handleUpdate({ status })}
            onChangePriority={(priority) => handleUpdate({ priority })}
            onChangeCategory={(categoryId) =>
              handleUpdate({ category_id: categoryId })
            }
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onToggleWatch={handleToggleWatch}
            onReassign={handleReassign}
          />
        </div>

        <div className="p-6">
          {QUERY_SUBJECT_TYPES.has(ticket.subject_type ?? "") && (
            <div className="mb-4 rounded-lg border border-border p-4 text-sm">
              <Link
                to={paths.objectRecord({
                  objectId: "workflow.ticket",
                  recordKey: ticket.id,
                })}
                className="font-medium underline hover:no-underline"
              >
                {ANSWER_QUERY_IN_ONE}
              </Link>
            </div>
          )}
          {ticket.subject_type === "credit_hold" && (
            <CreditHoldDecisionPanel
              ticketId={ticket.id}
              onDecided={() => void handleCreditDecided()}
            />
          )}
          {/* The campaign banner at the top already says what happened on
              the same closed ticket, with its outcome; don't say it twice. */}
          {ticketOutcomeContext?.related_resolved_ticket &&
            campaignTicket?.earlier_closed_ticket?.ticket_id !==
              ticketOutcomeContext.related_resolved_ticket.id && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
              >
                <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p>
                    This contact already has a{" "}
                    {STATUS_CONFIG[
                      ticketOutcomeContext.related_resolved_ticket
                        .status as TicketStatusValue
                    ]?.label?.toLowerCase() ??
                      ticketOutcomeContext.related_resolved_ticket.status}{" "}
                    ticket —{" "}
                    <Link
                      to={paths.ticketDetail({
                        id: ticketOutcomeContext.related_resolved_ticket.id,
                      })}
                      className="underline hover:no-underline font-medium"
                    >
                      {ticketOutcomeContext.related_resolved_ticket.title}
                    </Link>
                    . Check it before calling again.
                  </p>
                  {ticketOutcomeContext.related_resolved_ticket
                    .outcome_comment ? (
                    // A logged outcome is free text — clamped so a long one
                    // can't push the rest of the page down in a banner whose
                    // whole job is to be glanceable.
                    <p className="mt-1 italic text-amber-700 dark:text-amber-400 line-clamp-3">
                      "
                      {
                        ticketOutcomeContext.related_resolved_ticket
                          .outcome_comment
                      }
                      "
                    </p>
                  ) : (
                    <p className="mt-1 text-amber-700 dark:text-amber-400">
                      No outcome was logged on it.
                    </p>
                  )}
                </div>
              </div>
            )}
          <div
            className={
              hasSidebar
                ? "grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6"
                : ""
            }
          >
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as "details" | "activity")}
            >
              <TabsList variant="underline" className="mb-4">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="activity">
                  Activity
                  <TabsTriggerCount>
                    {activity.length + (ticket.notes?.length ?? 0)}
                  </TabsTriggerCount>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-5">
                {isEditing ? (
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add more context (optional)"
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                ) : description ? (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {description}
                  </p>
                ) : null}

                {campaignTicket && (
                  <CampaignTicketSection
                    context={campaignTicket}
                    onRecord={handleRecordCampaignOutcome}
                    onClear={handleClearCampaignOutcome}
                  />
                )}

                {/* Covering banner (csr-ticket-routing V2: manual
                    covering) — the CSR's assigned_to_user_id never
                    changes automatically; covering_user_id records who's
                    actually working it while they're away, and stays set
                    even after the CSR returns. Managers can assign/override
                    cover on any assigned ticket at any time, not only when
                    the CSR is detected absent today — so the picker also
                    shows for a manager whenever the ticket has a CSR. */}
                {(ticket.covering_user_id ||
                  ticket.assignee_absent_today ||
                  (canManageAnyTicket && ticket.assigned_to_user_id)) && (
                  <div className="flex items-start gap-2 p-3 border border-border rounded-lg bg-muted/50 text-sm">
                    <UserCog className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <span>
                        {ticket.covering_user_id ? (
                          <>
                            Covered by{" "}
                            {ticket.covering_user_name ??
                              resolveUserName(ticket.covering_user_id)}
                            {ticket.assignee_absent_today
                              ? ` while ${resolveUserName(ticket.assigned_to_user_id ?? "")} is away`
                              : ""}
                          </>
                        ) : ticket.assignee_absent_today ? (
                          `${resolveUserName(ticket.assigned_to_user_id ?? "")} is away today — no cover assigned yet`
                        ) : (
                          `${resolveUserName(ticket.assigned_to_user_id ?? "")} owns this ticket — assign a buddy to cover it if they're away`
                        )}
                      </span>

                      {canManageAnyTicket &&
                        (!ticket.covering_user_id || isChangingCovering) &&
                        csrBuddyList &&
                        csrBuddyList.buddyUserIds.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Select
                              value={selectedCoveringUserId}
                              onValueChange={setSelectedCoveringUserId}
                            >
                              <SelectTrigger className="h-8 text-sm w-[200px]">
                                <SelectValue placeholder="Choose a buddy" />
                              </SelectTrigger>
                              <SelectContent>
                                {csrBuddyList.buddyUserIds.map((buddyId) => (
                                  <SelectItem key={buddyId} value={buddyId}>
                                    {resolveUserName(buddyId)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={handleAssignCovering}
                              disabled={
                                !selectedCoveringUserId || isAssigningCovering
                              }
                            >
                              Assign covering
                            </Button>
                            {isChangingCovering && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setIsChangingCovering(false);
                                  setSelectedCoveringUserId("");
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        )}

                      {canManageAnyTicket &&
                        ticket.covering_user_id &&
                        !isChangingCovering && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setIsChangingCovering(true)}
                          >
                            Change
                          </Button>
                        )}

                      {canManageAnyTicket &&
                        !ticket.covering_user_id &&
                        csrBuddyListLoaded &&
                        (!csrBuddyList ||
                          csrBuddyList.buddyUserIds.length === 0) && (
                          <p className="text-xs text-muted-foreground">
                            {resolveUserName(ticket.assigned_to_user_id ?? "")}{" "}
                            has no buddies configured — add some in Settings →
                            Buddies to assign covering.
                          </p>
                        )}
                    </div>
                  </div>
                )}

                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleSaveEdit}
                      size="sm"
                      disabled={!title.trim()}
                    >
                      Save Changes
                    </Button>
                    <Button
                      onClick={handleCancelEdit}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {!isCompleted && (
                      <Button onClick={handleComplete} size="sm">
                        <CheckCircle className="size-4 mr-2" />
                        Mark as Resolved
                      </Button>
                    )}
                    {isCompleted && (
                      <Button
                        onClick={handleReopen}
                        variant="outline"
                        size="sm"
                      >
                        Reopen Ticket
                      </Button>
                    )}
                    <TicketCalendarButton ticket={ticket} />
                  </div>
                )}

                {/* Department ownership (department-ticket-routing) — the
                    fallback routing target when a ticket can't reach an
                    individual. Claim is gated on membership, never shown to
                    someone it would 403 for. */}
                {ticket.owning_department_name && (
                  <div className="flex items-start gap-2 p-3 border border-border rounded-lg bg-muted/50 text-sm">
                    <Building2 className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <span>
                        Routed to{" "}
                        <span className="font-medium">
                          {ticket.owning_department_name}
                        </span>
                        {ticket.assigned_to_user_id
                          ? ""
                          : " — nobody has claimed it yet"}
                      </span>
                      {escalationHint && (
                        <p
                          className={`text-xs ${
                            escalationHint === "escalation overdue"
                              ? "text-destructive"
                              : "text-muted-foreground"
                          }`}
                        >
                          {escalationHint}
                        </p>
                      )}
                      {claimConflictMessage && (
                        <p className="text-xs text-destructive">
                          {claimConflictMessage}
                        </p>
                      )}
                      {canClaim && (
                        <Button
                          size="sm"
                          onClick={handleClaim}
                          disabled={isClaiming}
                        >
                          <UserCheck className="size-4 mr-2" />
                          {isClaiming ? "Claiming…" : "Claim"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {!ticket.customer_id && !isCompleted && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Customer</h3>
                    <EntityCombobox
                      value={null}
                      onChange={handleAssignCustomer}
                      fetchOptions={fetchCustomers}
                      placeholder="Add a customer…"
                      ariaLabel="Customer this ticket relates to"
                      className="w-full"
                    />
                    {assignCustomerError && (
                      <p className="text-xs text-destructive mt-1">
                        {assignCustomerError}
                      </p>
                    )}
                  </div>
                )}

                {ticket.customer_name && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Customer</h3>
                    <p className="text-sm mb-2">{ticket.customer_name}</p>
                    {(ticket.primary_contact_email ||
                      ticket.primary_contact_phone) && (
                      <div className="space-y-1">
                        {ticket.primary_contact_email && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="size-3.5" />
                            <a
                              href={`mailto:${ticket.primary_contact_email}`}
                              className="hover:text-primary transition-colors"
                            >
                              {ticket.primary_contact_email}
                            </a>
                          </div>
                        )}
                        {ticket.primary_contact_phone && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="size-3.5" />
                            <a
                              href={`tel:${ticket.primary_contact_phone}`}
                              className="hover:text-primary transition-colors"
                            >
                              {ticket.primary_contact_phone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {ticket.customer_person_id != null && (
                  <p className="text-sm">
                    <Link
                      to={paths.contactDetail({
                        id: ticket.customer_person_id,
                        customerId: ticket.customer_id ?? undefined,
                      })}
                      className="text-primary hover:underline"
                    >
                      View this contact's full history
                    </Link>
                  </p>
                )}

                {/* A "call" ticket with no phone number on file (neither
                    Tharstern nor the marketing contact) leaves the rep with
                    no way to complete the action as titled — surface that
                    explicitly rather than silently showing nothing, so they
                    know to fall back to email or close the ticket. */}
                {ticket.action_type === "call" &&
                  !ticket.primary_contact_phone && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                      <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                      <p>
                        No phone number found in One or Tharstern.
                        {ticket.primary_contact_email
                          ? ` Reach out by email instead, or close this ticket if you can't reach them.`
                          : " No email on file either — please close this ticket."}
                      </p>
                    </div>
                  )}

                {relatedContactTickets.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">
                      Related tickets for this contact (
                      {relatedContactTickets.length})
                    </h3>
                    <ul className="space-y-1.5">
                      {relatedContactTickets.map((t) => {
                        const campaignName = campaignTicketLabel(t);
                        return (
                          <li key={t.id}>
                            <Link
                              to={paths.ticketDetail({ id: t.id })}
                              className="flex items-center justify-between gap-2 text-sm hover:text-primary transition-colors"
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                <span className="truncate">{t.title}</span>
                                {campaignName && (
                                  <Badge
                                    variant="outline"
                                    className="shrink-0 gap-1 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300"
                                    title={campaignName}
                                  >
                                    <Megaphone className="size-3 shrink-0" />
                                    <span className="truncate max-w-[140px]">
                                      {campaignName}
                                    </span>
                                  </Badge>
                                )}
                              </span>
                              <Badge
                                variant={
                                  STATUS_CONFIG[t.status as TicketStatusValue]
                                    ?.variant ?? "secondary"
                                }
                                className="shrink-0"
                              >
                                {STATUS_CONFIG[t.status as TicketStatusValue]
                                  ?.label ?? t.status}
                              </Badge>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {campaignEngagementContact && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">
                      Campaign engagement
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {campaignEngagementContact.opened_count} open
                      {campaignEngagementContact.opened_count === 1
                        ? ""
                        : "s"}, {campaignEngagementContact.clicked_count} link
                      {campaignEngagementContact.clicked_count === 1
                        ? ""
                        : "s"}{" "}
                      clicked
                    </p>
                    {campaignEngagementContact.clicked_links.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {campaignEngagementContact.clicked_links.map((link) => (
                          <li
                            key={link}
                            className="text-xs text-muted-foreground truncate"
                            title={link}
                          >
                            {link}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <TicketComments
                  comments={activity.filter(
                    (a) => a.activity_type === "comment",
                  )}
                  notes={ticket.notes ?? []}
                  resolveUserName={resolveUserName}
                  onAddComment={handleAddComment}
                  onDownloadAttachment={handleDownloadAttachment}
                />

                <div className="pt-3 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    Created {formatDateTimeOrDash(ticket.created_at)}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="activity">
                <TicketActivityTimeline
                  activity={activity}
                  notes={ticket.notes ?? []}
                  resolveUserName={resolveUserName}
                />
                {(ticket.notes?.length ?? 0) > 0 && (
                  <p className="text-sm mt-3">
                    <Link
                      to={`${paths.notes()}?ticket=${ticket.id}`}
                      className="text-primary hover:underline"
                    >
                      Search this ticket's notes
                    </Link>
                  </p>
                )}
              </TabsContent>
            </Tabs>

            {hasSidebar && (
              <div className="space-y-4">
                {linkedJob && (
                  <div className="p-3 border rounded-lg bg-surface space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Briefcase className="size-4 text-primary" />
                      Linked Job
                    </div>
                    <div className="text-sm">
                      {linkedJob.job_no ?? linkedJob.id}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {linkedJob.description}
                    </p>
                    {linkedJob.due_at && (
                      <div className="text-xs text-muted-foreground">
                        Due {formatDateOrDash(linkedJob.due_at)}
                      </div>
                    )}
                    {isRouteEnabled("jobWorkspace") && linkedJob.job_no && (
                      <Link
                        to={paths.jobWorkspace({ jobNo: linkedJob.job_no })}
                      >
                        <Button variant="outline" size="sm" className="w-full">
                          View job
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
                {linkedEstimate && (
                  <div className="p-3 border rounded-lg bg-surface space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FileSpreadsheet className="size-4 text-primary" />
                      Linked Estimate
                    </div>
                    <div className="text-sm">{linkedEstimate.estimate_id}</div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {linkedEstimate.description}
                    </p>
                    <div className="text-xs text-muted-foreground">
                      £{linkedEstimate.estimate_amt.toLocaleString()}
                    </div>
                    <Link to={paths.estimateDetail({ id: linkedEstimate.id })}>
                      <Button variant="outline" size="sm" className="w-full">
                        View estimate
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
